import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Microscope, ShieldCheck, Eye, Clock, CheckCircle2, CalendarHeart } from "lucide-react";
import { SITE } from "../lib/site";

export function ColposcopiaInfoDialog({ trigger }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="colposcopia-info-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border-brand-silver bg-brand-snow p-0 [&>button]:hidden"
      >
        {/* Header */}
        <div className="relative px-7 pt-8 pb-6 bg-gradient-to-br from-brand-deep via-brand-medical to-brand-bright text-white rounded-t-3xl overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 ring-1 ring-white/20 flex items-center justify-center flex-shrink-0">
              <Microscope className="w-5 h-5" strokeWidth={1.4} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-brand-silver mb-2">Prevención · Diagnóstico</p>
              <DialogTitle asChild>
                <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                  Colposcopia y Papanicolaou
                </h2>
              </DialogTitle>
              <p className="font-serif italic text-lg text-brand-silver mt-2">
                &ldquo;La detección oportuna salva vidas.&rdquo;
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-7 space-y-7">
          {/* Papanicolaou */}
          <section data-testid="info-papanicolaou">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-brand-cloud ring-1 ring-brand-silver flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-brand-medical" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-brand-deep">Papanicolaou</h3>
            </div>
            <p className="text-brand-deep/80 font-light leading-relaxed mb-4">
              Es un estudio sencillo y rápido en el que se toma una pequeña muestra de
              células del cuello uterino para analizarlas en el laboratorio. Permite
              detectar de forma temprana cambios celulares que podrían convertirse en
              cáncer cervicouterino.
            </p>
            <div className="rounded-2xl bg-white hairline p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical mb-3">¿Para qué sirve?</p>
              <ul className="space-y-2">
                {[
                  "Detectar lesiones precancerosas antes de que se vuelvan cáncer.",
                  "Identificar infecciones por Virus del Papiloma Humano (VPH).",
                  "Detectar inflamaciones o infecciones vaginales.",
                  "Monitorear la salud del cuello uterino a lo largo del tiempo.",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-brand-deep/85 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-brand-medical mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand-medical bg-brand-cloud rounded-full px-3 py-1.5">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              Recomendado anualmente desde el inicio de la vida sexual.
            </p>
          </section>

          {/* Separator */}
          <div className="silver-line h-px" />

          {/* Colposcopia */}
          <section data-testid="info-colposcopia">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-brand-cloud ring-1 ring-brand-silver flex items-center justify-center">
                <Eye className="w-4 h-4 text-brand-medical" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl text-brand-deep">Colposcopia</h3>
            </div>
            <p className="text-brand-deep/80 font-light leading-relaxed mb-4">
              Es un procedimiento visual realizado con un instrumento llamado
              colposcopio, que amplifica la imagen del cuello uterino, la vagina y la
              vulva. Permite examinar a detalle las áreas sospechosas que el
              Papanicolaou pudo haber detectado.
            </p>
            <div className="rounded-2xl bg-white hairline p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical mb-3">¿Para qué sirve?</p>
              <ul className="space-y-2">
                {[
                  "Evaluar lesiones cervicales detectadas previamente.",
                  "Tomar biopsia dirigida cuando es necesario un diagnóstico definitivo.",
                  "Diagnosticar lesiones por Virus del Papiloma Humano (VPH).",
                  "Dar seguimiento a tratamientos previos del cuello uterino.",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-brand-deep/85 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-brand-medical mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand-medical bg-brand-cloud rounded-full px-3 py-1.5">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              Procedimiento ambulatorio, indoloro y de unos 15 minutos.
            </p>
          </section>

          {/* Importance */}
          <div className="rounded-2xl bg-gradient-to-br from-brand-cloud to-brand-silver/60 p-5 hairline">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-medical mb-2">¿Por qué son tan importantes?</p>
            <p className="font-serif text-lg text-brand-deep leading-snug">
              El cáncer cervicouterino es uno de los cánceres más prevenibles cuando
              se detecta a tiempo. Estos dos estudios, realizados de forma periódica,
              son la herramienta más efectiva para proteger tu salud.
            </p>
          </div>

          {/* CTA */}
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="modal-agendar-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-7 py-3.5 font-medium tracking-wide transition-all premium-shadow w-full"
          >
            <CalendarHeart className="w-4 h-4" /> Agenda tu estudio
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
